import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { Send, Loader2, ArrowRight, Users, Target, BarChart3 } from "lucide-react";
import { submitContactMessage } from "@/lib/contact.functions";
import { SITE_URL } from "@/lib/utils";

export const Route = createFileRoute("/advertise")({
  head: () => ({
    meta: [
      { title: "Advertise on Pluggz — Partner With UK Creators | Pluggz" },
      {
        name: "description",
        content:
          "Connect your brand with verified UK creators on Pluggz. Performance-based affiliate partnerships with real-time tracking and full attribution.",
      },
      { property: "og:title", content: "Advertise on Pluggz — Partner With UK Creators | Pluggz" },
      {
        property: "og:description",
        content:
          "Connect your brand with verified UK creators on Pluggz. Performance-based affiliate partnerships with real-time tracking and full attribution.",
      },
      { property: "og:image", content: `${SITE_URL}/og-image.jpg` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/advertise` }],
  }),
  component: AdvertisePage,
});

function AdvertisePage() {
  const contactRef = useRef<HTMLDivElement>(null);
  const submit = useServerFn(submitContactMessage);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    website: "",
    budget: "",
    message: "",
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || !email || message.length < 10) {
      toast.error("Please fill in your name, email, and a message (10+ characters).");
      return;
    }

    setSubmitting(true);
    try {
      const fullMessage = [
        form.company.trim() ? `Company: ${form.company.trim()}` : null,
        form.website.trim() ? `Website: ${form.website.trim()}` : null,
        form.budget ? `Budget: ${form.budget}` : null,
        message,
      ]
        .filter(Boolean)
        .join("\n\n");

      await submit({
        data: {
          name,
          email,
          subject: "Brand Partnership Enquiry",
          message: fullMessage,
        },
      });
      setSent(true);
      setForm({ name: "", company: "", email: "", website: "", budget: "", message: "" });
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="mesh-bg pointer-events-none absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-5xl text-center">
          <p className="editorial-eyebrow">For Brands</p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight tracking-tight md:text-6xl">
            Reach shoppers through creators they trust
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Pluggz connects brands with curated creator audiences across fashion, beauty, home
            and travel. Performance-based partnerships that drive real sales.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-6 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              Get in touch
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/creators"
              className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-card px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              View our creators
            </Link>
          </div>
        </div>
      </section>

      {/* Why Pluggz */}
      <section className="border-t border-border/60 px-6 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="editorial-eyebrow mb-8 text-center">Why Pluggz</p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon={<Target className="h-5 w-5" />}
              title="100% performance-based"
              body="Pay only when creators drive sales through affiliate commissions"
            />
            <StatCard
              icon={<Users className="h-5 w-5" />}
              title="Curated audiences"
              body="Every creator is hand-selected and verified by our editorial team"
            />
            <StatCard
              icon={<BarChart3 className="h-5 w-5" />}
              title="Full attribution"
              body="Real-time tracking across all social channels including TikTok in-app"
            />
          </div>
        </div>
      </section>

      {/* Placement options */}
      <section className="border-t border-border/60 px-6 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="editorial-eyebrow mb-8 text-center">Placement options</p>
          <div className="grid gap-6 md:grid-cols-3">
            <TierCard
              tier="Tier 1"
              name="Affiliate"
              price="Free to list"
              features={[
                "Commission on sales only",
                "Products synced from affiliate networks",
                "Creator-led curation",
              ]}
              highlight={false}
            />
            <TierCard
              tier="Tier 2"
              name="Featured"
              price="From £500/month"
              features={[
                "Homepage Featured Stories placement",
                "Priority in category filters",
                "Affiliate commission on sales",
              ]}
              highlight={true}
            />
            <TierCard
              tier="Tier 3"
              name="Partnership"
              price="Bespoke pricing"
              features={[
                "Dedicated creator partnerships",
                "Custom campaigns",
                "Full reporting dashboard",
              ]}
              highlight={false}
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border/60 px-6 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="editorial-eyebrow mb-8 text-center">How it works</p>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <StepCard number={1} title="Apply to list your brand" body="Submit your details and we'll review your products within 48 hours." />
            <StepCard number={2} title="Products sync automatically" body="Connect your affiliate network and products appear on Pluggz instantly." />
            <StepCard number={3} title="Creators curate and promote" body="Hand-picked creators add your products to their storefronts and share with their audiences." />
            <StepCard number={4} title="Track and pay on results" body="See real-time sales, clicks and commissions. Pay only for what converts." />
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section ref={contactRef} className="border-t border-border/60 px-6 py-20 md:py-28">
        <div className="mx-auto max-w-2xl">
          <p className="editorial-eyebrow mb-3 text-center">Get in touch</p>
          <h2 className="text-center font-serif text-3xl tracking-tight md:text-4xl">
            Start a brand partnership
          </h2>
          <p className="mt-3 text-center text-sm text-muted-foreground">
            Fill in the form below and our partnerships team will reply within 1–2 business days.
          </p>

          <div className="mt-10 rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
            {sent ? (
              <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
                <div className="rounded-full bg-primary/10 p-4 text-primary">
                  <Send className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-serif text-2xl">Thanks — message received.</h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Our partnerships team will be in touch shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-6 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" htmlFor="name">
                    <input
                      id="name"
                      name="name"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Company" htmlFor="company" optional>
                    <input
                      id="company"
                      name="company"
                      maxLength={120}
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className={inputCls}
                      autoComplete="organization"
                    />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Email" htmlFor="email">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={255}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="Website" htmlFor="website" optional>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      maxLength={255}
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      className={inputCls}
                      placeholder="https://"
                    />
                  </Field>
                </div>
                <Field label="Monthly budget" htmlFor="budget" optional>
                  <select
                    id="budget"
                    name="budget"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">Select a range…</option>
                    <option value="Under £500">Under £500</option>
                    <option value="£500 – £2,000">£500 – £2,000</option>
                    <option value="£2,000 – £10,000">£2,000 – £10,000</option>
                    <option value="£10,000+">£10,000+</option>
                  </select>
                </Field>
                <Field label="Message" htmlFor="message">
                  <textarea
                    id="message"
                    name="message"
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${inputCls} resize-y`}
                    placeholder="Tell us about your brand and what you're looking for…"
                  />
                  <p className="mt-1 text-right text-xs text-muted-foreground">
                    {form.message.length}/4000
                  </p>
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

/* ─── Reusable components ─── */

function StatCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="card-lift rounded-2xl border border-border/60 bg-card p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground">
        {icon}
      </div>
      <h3 className="mt-4 font-serif text-xl tracking-tight">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function TierCard({
  tier,
  name,
  price,
  features,
  highlight,
}: {
  tier: string;
  name: string;
  price: string;
  features: string[];
  highlight: boolean;
}) {
  return (
    <div
      className={`relative rounded-2xl border p-6 transition-colors ${
        highlight
          ? "border-foreground/20 bg-foreground/[0.03]"
          : "border-border/60 bg-card"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-6 inline-block rounded-full bg-foreground px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-background">
          Most popular
        </span>
      )}
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tier}</p>
      <h3 className="mt-2 font-serif text-2xl tracking-tight">{name}</h3>
      <p className="mt-1 text-sm font-medium text-foreground">{price}</p>
      <ul className="mt-5 space-y-2.5">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
            <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-accent" />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepCard({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: string;
}) {
  return (
    <div className="relative pl-10">
      <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-border/60 bg-background text-xs font-medium text-muted-foreground">
        {number}
      </span>
      <h3 className="font-serif text-lg tracking-tight">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 flex items-center justify-between text-xs font-medium uppercase tracking-widest text-muted-foreground"
      >
        <span>{label}</span>
        {optional ? (
          <span className="text-[10px] normal-case tracking-normal text-muted-foreground/70">
            Optional
          </span>
        ) : null}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-foreground/60 focus:ring-2 focus:ring-foreground/10";
